import type { IMapset, ICategoryItem, IInitialMapCoords, ISearchOnboarding, ILocation, ILocationMeta, IInitialMap } from "~/interfaces";
import type { ELoginState, IUserInitialPosition } from "~/state/user/interfaces";
import type { IHistoryItemData } from "~/util/providers/history";
import type { IMapserver } from "~common/types/mapserver";
import type { IUserSubscriptions } from "~common/types/user";
import { getItem, setItem, removeItem } from "~/util/cookies";

interface IABTest {
	name?: string;
	flags: string;
}

export interface IConfig {
	version: string;
	jsCompress: boolean;
	apiUrl: string;
	TLD: string;
	TLDCZ: string;
	DOMAIN: string;
	BASE_DOMAIN: string;
	BASE_URL: string;
	environment: "local" | "dev" | "testing" | "staging" | "stable";
	mapiKey: string;
	mapyURL: string;
	requestUrl: string;
	loginUrl: string;
	authInterval: number;
	loadMapLoginTimeout: number;
	printMode?: number;
	inFrame: boolean;
	UserwebSetting: {
		userweb: {
			[key: string]: number | string;
		};
		[key: string]: {
			[key: string]: number | string;
		};
	};
	UserwebSettingCookie: {
		userweb: {
			[key: string]: number | string;
		};
		[key: string]: {
			[key: string]: number | string;
		};
	};
	filesVersion: string;
	abTest?: IABTest;
	Left3d: {
		url: string;
		allowed: number;
	};
	location?: ILocation;
	locationMeta?: ILocationMeta;
	mapsets: Array<IMapset>;
	/** URL do Google Play appstore na naši appku */
	android: string;
	/** URL do iOS appstore na naši appku */
	ios: string;
	huawei: string;
	/** příznak, zda se web vykresluje ve screenshoteru */
	isScreenshoter: boolean;
	initialMapset: string;
	initialOverlays: Array<number>;
	ophotoTimeline?: string;
	user?: {
		avatar: string;
		bankId: boolean;
		email: string;
		name: string;
		rusId: number;
		state: ELoginState;
		userPublicId: string;
		accountDisplayName: string;
		sznEmployee?: boolean;
		phoneOnly: boolean;
		sbr: boolean;
		premium: boolean;
		premiumData: IUserSubscriptions;
	};
	authData?: any;
	/** úvodní souřadnice pro mapu, když se načte web, použijí se, pokud v URL nejsou žádné uvedené */
	initialMap: IInitialMapCoords;
	/** nastavení přihlášeného uživatele pro počáteční nastavení mapy po příchodu na web bez parametrů v URL */
	map?: IUserInitialPosition | Record<string, never>;
	/** nastavení přihlášeného uživatele pro potlačení mapy při hledání */
	mapSuppression: boolean;
	mapsetsIDsMenuList: Array<number>;
	mapsetsBlackList: Array<string>;
	routeDefaultCriterion: number;
	userHistory?: {
		category?: Array<ICategoryItem>;
		items?: Array<IHistoryItemData>;
	};
	searchOnboarding?: ISearchOnboarding;
	langPriority: Array<string>;
	defaultLang: string;
	urlData?: string;
	isMapyczApp?: number;
	dontShowAndroidPromo?: number;
	isEditorial: boolean;
	abCountry: string;
	poiicons?: {
		[key: string]: {
			[key: string]: number;
		};
	};
	loginLang: string;
	mapserver: IMapserver;
	panoCompose: string;
	cookiesExpiration: number;
	paddleToken: string;
	paddleEnvironment: "sandbox" | "production";
	availableProducts?: Array<string>;
	detectLang: string;
	geoConfig: {
		x: number;
		y: number;
		mobileMinWidth: number;
	};
	wasmEnabled: boolean;
	wasmCookie: string;
	euconsentV2: string;
}

export interface IMapset3D extends IMapset {
	logName: string;
	m3d: boolean;
	button?: HTMLLIElement;
}

interface IGetConfigDataOutput {
	abTest: IABTest;
	fromBrowser: boolean;
	config: IConfig["UserwebSetting"];
}

const SS_NAME = "UserwebConfigSettings";

export function setUserwebConfigData(saveData: IConfig["UserwebSetting"]) {
	try {
		sessionStorage.setItem(SS_NAME, JSON.stringify(saveData));
	} catch (exc) {
		//
	}
}

export function getUserwebConfigData(): null | IConfig["UserwebSetting"] {
	try {
		const storageStr = sessionStorage.getItem(SS_NAME) || "";

		if (storageStr) {
			return JSON.parse(storageStr);
		}
	} catch (ex) {
		//
	}

	return null;
}

export function removeUserwebConfigData() {
	try {
		sessionStorage.removeItem(SS_NAME);
	} catch (ex) {
		//
	}
}

// vraci bud ze sablony nebo z localstorage (cely objekt)
function getConfigData(): IGetConfigDataOutput {
	// pokud mame lokalni nastaveni klikatka, tak ho pouzijeme misto toho ze sablony
	const storageConfig = getUserwebConfigData();
	const config = storageConfig || window.MapyConfig.UserwebSetting;

	// pokud jsou v klikatku zadane AB testy, tak je pouzijeme prednostne misto tech z sablony
	let abTest: IABTest = null;

	if (config.userweb?.abTestsString && config.userweb.abTestsString !== "") {
		abTest = {
			flags: config.userweb?.abTestsString as string,
		};
	}

	return {
		abTest,
		fromBrowser: !!storageConfig,
		config,
	};
}

function getBEConfig(userwebSettingsData: IGetConfigDataOutput): Partial<IConfig> {
	const source: Partial<IConfig> = window.MapyConfigBE || {};
	const isRootLocation = /^\/[a-zA-Z]{2}\/$/u.test(location.pathname);
	// mapsetum obcas chybi obrazek, drive si to v sobe narovnaval mapsetswitch, ted to narovname uz na vstupu do aplikace
	const userwebSettings = userwebSettingsData.config;

	// pridame, pokud neexistuji
	if (!("mapsets" in source)) {
		source.mapsets = [];
	}

	const mapsets = source.mapsets;

	// base + letecka
	if (mapsets.length === 0) {
		mapsets.push({
			base: "base",
			hidden: false,
			icon: "zakladni",
			id: 1,
			layers: [],
			name: "Základní",
			seo: "zakladni",
			sources: [
				"base",
				"firm",
				"pubt",
				"osmp",
				"traf",
			],
			tip: false,
		});
		mapsets.push({
			base: "ophoto",
			hidden: false,
			icon: "letecka",
			id: 2,
			layers: [
				"hybrid-sparse-%suffix",
			],
			name: "Letecká",
			seo: "letecka",
			sources: [
				"base",
				"firm",
				"pubt",
				"traf",
				"osmp",
			],
			/* eslint-disable-next-line */
			is_ophoto: true,
			tip: false,
		});
	}

	mapsets.map(mapset => {
		if (!mapset.icon_url) {
			// eslint-disable-next-line camelcase
			mapset.icon_url = `/img/mapset/${mapset.icon || "zakladni"}-small.png?v${window.MapyConfig.filesVersion}`;
		}
	});

	// editor
	if (source.isEditorial && userwebSettings && !userwebSettingsData.fromBrowser) {
		userwebSettings.editorial = {
			interface: 1,
		};
	}

	if (source.initialMapset && !isRootLocation) {
		delete source.initialMapset;
	}

	if (source.initialOverlays && !isRootLocation) {
		delete source.initialOverlays;
	}

	return source;
}

// jine lokalizace maji jine uvodni souradnice
const MAP_DEFAULT_POSITION: {
	[key: string]: IInitialMap;
} = {
	cs: {
		lon: 15.6252330,
		lat: 49.8022514,
		zoom: 8,
	},
	sk: {
		lon: 19.4402339,
		lat: 48.8084443,
		zoom: 8,
	},
	pl: {
		lon: 18.8112666,
		lat: 52.1669813,
		zoom: 8,
	},
	de: {
		lon: 11.2307002,
		lat: 48.9150524,
		zoom: 8,
	},
	it: {
		lon: 11.8486812,
		lat: 43.2649542,
		zoom: 7,
	},
	uk: {
		lon: 29.6685054,
		lat: 48.8753267,
		zoom: 7,
	},
	ru: {
		lon: 36.9908931,
		lat: 55.7408330,
		zoom: 8,
	},
	en: {
		lon: 11.3817622,
		lat: 48.5563849,
		zoom: 7,
	},
};
const defaultLang = Userweb?.defaultLang || "cs";
const configData = getConfigData();
const beConfig = getBEConfig(configData);
const WASM_COOKIE = "enableWASM";
const isWasmCookieSet = getItem(WASM_COOKIE) === "1";
const isWasmEnabled = isWasmCookieSet || configData.config.userweb?.wasm === 1;

if (isWasmEnabled && !isWasmCookieSet) {
	setItem(WASM_COOKIE, "1");
}

if (isWasmEnabled) {
	if (configData.config.userweb?.wasmRelease === 1) {
		setItem("wasmRelease", "1");
	} else {
		removeItem("wasmRelease");
	}
}

export const CONFIG: IConfig = {
	android: "https://play.google.com/store/apps/details?id=cz.seznam.mapy",
	ios: "https://apps.apple.com/cz/app/mapy-cz/id411411020",
	huawei: "https://appgallery.huawei.com/#/app/C101957275",
	isScreenshoter: window && window.navigator && window.navigator.userAgent.indexOf("szn-screenshoter-node-chrome") > -1,
	langPriority: Userweb?.langPriority || ["cs", "sk", "en"],
	defaultLang,
	// podle jazyka + fallback
	initialMap: defaultLang in MAP_DEFAULT_POSITION
		? MAP_DEFAULT_POSITION[defaultLang]
		: MAP_DEFAULT_POSITION.cs,
	isEditorial: false,
	paddleToken: "",
	paddleEnvironment: "production",
	// merge z SABLONY - obsahuje userweb settings
	...window.MapyConfig,
	// klic dame az po sablone
	UserwebSetting: configData.config,
	// merge z BE
	...beConfig,
	...configData.abTest ? { abTest: configData.abTest } : {},
	mapserver: beConfig.mapserver || {
		url: `https://mapserver.mapy.${window.MapyConfig.TLD}`,
		country: "cz",
		crossOrigin: "anonymous",
		useTileCache: false,
		fallbackUrl: `https://mapserver.mapy.${window.MapyConfig.TLD}`,
		fallbackCrossOrigin: "anonymous",
	},
	// nove je proxypass na vsechny backendy /api
	requestUrl: "/api",
	wasmEnabled: isWasmEnabled,
	wasmCookie: WASM_COOKIE,
};

const mapsetName = {
	turisticka: "",
	zimni: "",
	letecka: "",
};
const mapset3d = Object.keys(mapsetName);

CONFIG.mapsets.forEach(mapset => {
	if (mapset3d.indexOf(mapset.seo) > -1) {
		mapsetName[mapset.seo] = mapset.name;
	}
});

export const CONF3D: Array<IMapset3D> = [{
	base: "",
	hidden: false,
	icon: "turisticka-3d",
	id: 0,
	layers: [],
	name: mapsetName.turisticka,
	seo: "turisticka",
	logName: "turisticka_3D",
	sources: [],
	m3d: true,
	tip: false,
}, {
	base: "",
	hidden: false,
	icon: "zimni-3d",
	id: 1,
	layers: [],
	name: mapsetName.zimni,
	seo: "zimni",
	logName: "zimni_3D",
	sources: [],
	m3d: true,
	tip: false,
}, {
	base: "",
	hidden: false,
	icon: "letecka-3d",
	id: 2,
	layers: [],
	name: mapsetName.letecka,
	seo: "letecka",
	logName: "ophoto_3D",
	sources: [],
	m3d: true,
	tip: false,
}];

export function setMapserverFallback() {
	CONFIG.mapserver.url = CONFIG.mapserver.fallbackUrl;
	CONFIG.mapserver.crossOrigin = CONFIG.mapserver.fallbackCrossOrigin;
}
